import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Loader, Zap } from 'lucide-react';

interface AutoGenerationFormProps {
  data: any;
  onUpdate: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
  onGenerating: (isGenerating: boolean) => void;
  isGenerating: boolean;
}

interface BlockConfig {
  blockId: number;
  blockName: string;
  totalFloors: number;
  prices: {
    studio: number;
    f2: number;
    f3: number;
    f4: number;
    f5: number;
    commercial: number;
  };
  pricePerFloor: number;
  seaViewBonus: number;
  seaViewFromFloor: number;
}

export const AutoGenerationForm: React.FC<AutoGenerationFormProps> = ({
  data,
  onUpdate,
  onNext,
  onBack,
  onGenerating,
  isGenerating,
}) => {
  const [blockConfigs, setBlockConfigs] = useState<BlockConfig[]>([]);
  const [generatedUnits, setGeneratedUnits] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (data.blocks && data.blocks.length > 0) {
      const configs = data.blocks.map((block: any, index: number) => ({
        blockId: index,
        blockName: block.block_name,
        totalFloors: block.total_floors,
        prices: {
          studio: 7000000,
          f2: 8500000,
          f3: 11000000,
          f4: 16000000,
          f5: 22000000,
          commercial: 15000000,
        },
        pricePerFloor: 5,
        seaViewBonus: 10,
        seaViewFromFloor: 4,
      }));
      setBlockConfigs(configs);
    }
  }, [data.blocks]);

  const updatePrice = (blockId: number, type: string, value: number) => {
    setBlockConfigs(prev =>
      prev.map(config =>
        config.blockId === blockId
          ? { ...config, prices: { ...config.prices, [type]: value } }
          : config
      )
    );
  };

  const updateAdjustment = (blockId: number, field: string, value: number) => {
    setBlockConfigs(prev =>
      prev.map(config =>
        config.blockId === blockId
          ? { ...config, [field]: value }
          : config
      )
    );
  };

  const generateUnitNumber = (blockName: string, floor: number, unitInFloor: number) => {
    return `${blockName}${floor.toString().padStart(2, '0')}${unitInFloor.toString().padStart(2, '0')}`;
  };

  const calculatePrice = (config: BlockConfig, type: string, floor: number, isDuplex: boolean, isTriplex: boolean) => {
    let price = config.prices[type as keyof typeof config.prices];

    if (floor > 0) {
      price += price * (config.pricePerFloor / 100) * floor;
    }

    if (floor >= config.seaViewFromFloor) {
      price += price * (config.seaViewBonus / 100);
    }

    if (isDuplex) {
      price *= 1.4;
    } else if (isTriplex) {
      price *= 1.6;
    }

    return Math.round(price);
  };

  const getDefaultArea = (type: string, isDuplex: boolean, isTriplex: boolean) => {
    const baseAreas: { [key: string]: number } = {
      studio: 35,
      f2: 65,
      f3: 85,
      f4: 110,
      f5: 145,
      commercial: 80,
    };
    let area = baseAreas[type] || 70;
    if (isDuplex) area *= 1.8;
    if (isTriplex) area *= 2.2;
    return Math.round(area);
  };

  const getDefaultRooms = (type: string) => {
    const rooms: { [key: string]: number } = {
      studio: 1,
      f2: 2,
      f3: 3,
      f4: 4,
      f5: 5,
      commercial: 1,
    };
    return rooms[type] || 2;
  };

  const getDefaultBedrooms = (type: string) => {
    const bedrooms: { [key: string]: number } = {
      studio: 0,
      f2: 1,
      f3: 2,
      f4: 3,
      f5: 4,
      commercial: 0,
    };
    return bedrooms[type] || 1;
  };

  const getDefaultOrientation = (floor: number) => {
    const orientations = ['nord', 'sud', 'est', 'ouest', 'sud-est'];
    return orientations[floor % orientations.length];
  };

  const generateAllUnits = async () => {
    setError(null);
    onGenerating(true);

    try {
      const allUnits: any[] = [];

      for (const config of blockConfigs) {
        const block = data.blocks[config.blockId];
        let unitNumberInFloor = 1;

        for (let floor = 0; floor <= config.totalFloors; floor++) {
          const types = ['studio', 'f2', 'f3', 'f4', 'f5', 'commercial'];

          for (const type of types) {
            const totalCount = block[`${type}_count`] || 0;
            const duplexCount = block[`${type}_duplex_count`] || 0;
            const triplexCount = block[`${type}_triplex_count`] || 0;
            const normalCount = totalCount - duplexCount - triplexCount;

            for (let i = 0; i < normalCount; i++) {
              const unitNumber = generateUnitNumber(block.block_name, floor, unitNumberInFloor);
              const price = calculatePrice(config, type, floor, false, false);
              const area = getDefaultArea(type, false, false);

              allUnits.push({
                block_name: block.block_name,
                unit_number: unitNumber,
                floor,
                unit_type: type,
                is_duplex: false,
                is_triplex: false,
                floor_span: 1,
                area,
                rooms: getDefaultRooms(type),
                bedrooms: getDefaultBedrooms(type),
                bathrooms: 1,
                orientation: getDefaultOrientation(floor),
                view: floor >= config.seaViewFromFloor ? 'Mer' : 'Dégagée',
                has_balcony: type !== 'commercial',
                balcony_area: type !== 'commercial' ? 8 : 0,
                has_parking: true,
                parking_included: true,
                parking_number: `P-${block.block_name}${unitNumberInFloor.toString().padStart(2, '0')}`,
                parking_type: 'sous-sol',
                base_price: config.prices[type as keyof typeof config.prices],
                final_price: price,
                status: 'available',
              });

              unitNumberInFloor++;
            }

            for (let i = 0; i < duplexCount; i++) {
              const unitNumber = generateUnitNumber(block.block_name, floor, unitNumberInFloor);
              const price = calculatePrice(config, type, floor, true, false);
              const area = getDefaultArea(type, true, false);

              allUnits.push({
                block_name: block.block_name,
                unit_number: unitNumber,
                floor,
                unit_type: type,
                is_duplex: true,
                is_triplex: false,
                floor_span: 2,
                area,
                rooms: getDefaultRooms(type) + 1,
                bedrooms: getDefaultBedrooms(type) + 1,
                bathrooms: 2,
                orientation: getDefaultOrientation(floor),
                view: floor >= config.seaViewFromFloor ? 'Mer panoramique' : 'Dégagée',
                has_balcony: true,
                balcony_area: 15,
                has_parking: true,
                parking_included: true,
                parking_number: `P-${block.block_name}${unitNumberInFloor.toString().padStart(2, '0')}`,
                parking_type: 'sous-sol',
                base_price: config.prices[type as keyof typeof config.prices],
                final_price: price,
                status: 'available',
              });

              unitNumberInFloor++;
            }

            for (let i = 0; i < triplexCount; i++) {
              const unitNumber = generateUnitNumber(block.block_name, floor, unitNumberInFloor);
              const price = calculatePrice(config, type, floor, false, true);
              const area = getDefaultArea(type, false, true);

              allUnits.push({
                block_name: block.block_name,
                unit_number: unitNumber,
                floor,
                unit_type: type,
                is_duplex: false,
                is_triplex: true,
                floor_span: 3,
                area,
                rooms: getDefaultRooms(type) + 2,
                bedrooms: getDefaultBedrooms(type) + 2,
                bathrooms: 3,
                orientation: getDefaultOrientation(floor),
                view: 'Mer panoramique',
                has_balcony: true,
                balcony_area: 25,
                has_terrace: true,
                terrace_area: 40,
                has_parking: true,
                parking_included: true,
                parking_number: `P-${block.block_name}${unitNumberInFloor.toString().padStart(2, '0')}`,
                parking_type: 'sous-sol',
                base_price: config.prices[type as keyof typeof config.prices],
                final_price: price,
                status: 'available',
              });

              unitNumberInFloor++;
            }
          }
        }
      }

      setGeneratedUnits(allUnits);
      setSuccess(true);
      onUpdate({ ...data, units: allUnits });

    } catch (err: any) {
      setError(err.message || 'Erreur lors de la génération des lots');
    } finally {
      onGenerating(false);
    }
  };

  if (success) {
    return (
      <div className="space-y-6">
        <div className="bg-green-50 dark:bg-green-900/30 border-2 border-green-500 rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            <div>
              <h3 className="text-xl font-bold text-green-900 dark:text-green-100">
                {generatedUnits.length} lots générés avec succès !
              </h3>
              <p className="text-green-700 dark:text-green-300 text-sm">
                Tous les lots ont été créés automatiquement selon vos critères
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {['studio', 'f2', 'f3', 'f4', 'f5'].map(type => {
              const count = generatedUnits.filter(u => u.unit_type === type).length;
              return count > 0 ? (
                <div key={type} className="bg-white dark:bg-gray-800 rounded-lg p-3">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {count}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 uppercase">
                    {type}
                  </div>
                </div>
              ) : null;
            })}
          </div>

          <p className="mt-4 text-sm text-green-700 dark:text-green-300">
            Vous pourrez modifier chaque lot individuellement dans la page de gestion de la promotion
          </p>
        </div>

        <div className="flex justify-between pt-6 border-t dark:border-gray-700">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            ← Retour
          </button>

          <button
            onClick={onNext}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Suivant : Photos →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Génération automatique des lots
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Définissez les prix de base et les ajustements pour chaque bloc
        </p>
      </div>

      {blockConfigs.map((config) => (
        <div key={config.blockId} className="border dark:border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
            Bloc {config.blockName}
          </h3>

          <div className="space-y-3 mb-6">
            <h4 className="font-medium text-gray-900 dark:text-white">Prix de base par type :</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {['f2', 'f3', 'f4', 'f5'].map(type => (
                <div key={type}>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1 uppercase">
                    {type}
                  </label>
                  <input
                    type="number"
                    value={config.prices[type as keyof typeof config.prices]}
                    onChange={(e) => updatePrice(config.blockId, type, parseInt(e.target.value))}
                    className="w-full px-3 py-2 border dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 dark:text-white">Ajustements de prix :</h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                  +% par étage
                </label>
                <input
                  type="number"
                  value={config.pricePerFloor}
                  onChange={(e) => updateAdjustment(config.blockId, 'pricePerFloor', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                  Bonus vue mer (%)
                </label>
                <input
                  type="number"
                  value={config.seaViewBonus}
                  onChange={(e) => updateAdjustment(config.blockId, 'seaViewBonus', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                  Vue mer à partir étage
                </label>
                <input
                  type="number"
                  value={config.seaViewFromFloor}
                  onChange={(e) => updateAdjustment(config.blockId, 'seaViewFromFloor', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>
        </div>
      ))}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border-2 border-red-500 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-bold text-red-900 dark:text-red-100 mb-1">Erreur</h4>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between pt-6 border-t dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          disabled={isGenerating}
          className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
        >
          ← Retour
        </button>

        <button
          onClick={generateAllUnits}
          disabled={isGenerating}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
        >
          {isGenerating ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>Génération en cours...</span>
            </>
          ) : (
            <>
              <Zap className="w-5 h-5" />
              <span>Générer tous les lots</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};
